import Logger from "@cappelendamm/logger";
/**
 * Merges customer import data (metadata and external references) with existing customer data.
 * Only adds new fields that don't already exist on the customer.
 *
 * @param existingCustomer The existing customer from the database
 * @param newCustomerData The new customer data from import
 * @returns UpdateCustomerInput with merged metadata and external references
 */
export function mergeCustomerImportData(existingCustomer, newCustomerData) {
    const existingMeta = existingCustomer.meta ?? [];
    const existingExternalRefs = existingCustomer.externalReferences ?? [];
    const newMeta = newCustomerData.meta ?? [];
    const newExternalRefs = newCustomerData.externalReferences ?? [];
    // Create maps for existing data to check both keys and values
    const existingMetaMap = new Map(existingMeta.map((item) => [item.key, item.value]));
    const existingExternalRefMap = new Map(existingExternalRefs.map((item) => [item.key, item.value]));
    // Filter out meta fields that already exist and log value differences
    const metaToAdd = newMeta.filter((item) => {
        if (existingMetaMap.has(item.key)) {
            const existingValue = existingMetaMap.get(item.key);
            if (existingValue !== item.value) {
                Logger.log(`Skipping meta field "${item.key}" for customer ${existingCustomer.identifier}: existing value "${existingValue}" differs from import value "${item.value}"`);
            }
            return false; // Skip this item
        }
        return true; // Add this item
    });
    // Filter out external references that already exist and log value differences
    const externalRefsToAdd = newExternalRefs.filter((item) => {
        if (existingExternalRefMap.has(item.key)) {
            const existingValue = existingExternalRefMap.get(item.key);
            if (existingValue !== item.value) {
                Logger.log(`Skipping external reference "${item.key}" for customer ${existingCustomer.identifier}: existing value "${existingValue}" differs from import value "${item.value}"`);
            }
            return false; // Skip this item
        }
        return true; // Add this item
    });
    // If there's nothing to add, return null to indicate no update needed
    if (metaToAdd.length === 0 && externalRefsToAdd.length === 0) {
        Logger.log(`No new metadata or external references to add for customer ${existingCustomer.identifier}`);
        return null;
    }
    Logger.log(`Adding ${metaToAdd.length} meta fields and ${externalRefsToAdd.length} external references to customer ${existingCustomer.identifier}`);
    // Create merged data arrays
    const mergedMeta = [...existingMeta, ...metaToAdd];
    const mergedExternalRefs = [...existingExternalRefs, ...externalRefsToAdd];
    return {
        meta: mergedMeta.map((item) => ({
            key: item.key,
            value: item.value ?? undefined,
        })),
        externalReferences: mergedExternalRefs.map((item) => ({
            key: item.key,
            value: item.value ?? undefined,
        })),
    };
}
